/* eslint-disable camelcase */
import type {
  Carer,
  SkillNeedTemplate_TrainingMatrix_SkillMatrix_Status
} from '@lmc/ts-common'
import type { TrainingColumn, TrainingRow } from '@lmc/ui-kit'
import type { DotStatus } from '@lmc/ui-kit/src/molecules/Legend/Legend'
import type { ResidentCellProps } from '@lmc/ui-kit/src/molecules/Table/cells/ResidentCell'
import type { DotCellProps } from '@lmc/ui-kit/src/organisms/TrainingMatrix/components/DotCell'
import type {
  DueOverdueByCarerResponse,
  DueOverdueByTrainingResponse
} from '../components/DueOverdueTrainingMatrix'
import type { SkillMatrixResponse } from '../TrainingMatrixScreen'

const validStatuses: (keyof typeof SkillNeedTemplate_TrainingMatrix_SkillMatrix_Status)[] =
  ['valid', 'dueSoon']

export const getColumnsStatus = (content: SkillMatrixResponse['content']) => {
  const totalSkillsNumberDictionary: Dictionary<number> = {}
  content.forEach((element) => {
    Object.keys(element.carerSkills).forEach((skillId) => {
      const currentElementStatus = element.carerSkills[skillId].status

      if (validStatuses.includes(currentElementStatus)) {
        if (totalSkillsNumberDictionary[skillId]) {
          totalSkillsNumberDictionary[skillId] += 1
        } else {
          totalSkillsNumberDictionary[skillId] = 1
        }
      }
    })
  })

  return totalSkillsNumberDictionary
}

export const getDotTitle = (
  status: DotStatus,
  trainingRequired: boolean,
  isAchiedDateSet: boolean
) => {
  switch (status) {
    case 'valid':
      if (trainingRequired) {
        return 'trainingValid'
      }

      return 'trainingNotRequired'
    case 'dueSoon':
      return 'trainingDueSoon'
    case 'notAssigned':
      return 'skillNotAssigned'
    case 'notValid':
      if (!isAchiedDateSet) {
        return 'achievedDateNotSet'
      }

      return 'trainingOverdue'
    default:
      return ''
  }
}

const getTrainingRequired = (
  skillId: string,
  data: SkillMatrixResponse
): boolean => {
  const template = data.templates.find((template) => template.id === skillId)
  const trainingRequired = template?.trainingRequired

  return trainingRequired || false
}

const getDueForRefresh = (
  status: DotStatus,
  trainingDueDate: string | undefined,
  t: (value: string) => string
) => {
  if (status === 'valid' && !trainingDueDate) {
    return t('skillsAndNeeds.dots.noExpire')
  }

  return trainingDueDate
}

export const getRows = (
  data: SkillMatrixResponse,
  carers: Carer[] | undefined,
  t: (value: string) => string
) => {
  const rows: TrainingRow[] = data.content.map((element) => {
    const mappedRowsObject: Record<string, DotCellProps> = {}

    Object.keys(element.carerSkills).forEach((skillId) => {
      const skill = element.carerSkills[skillId]
      const { status, trainingCompletedDate, trainingDueDate } = skill

      const trainingRequired = getTrainingRequired(skillId, data)
      const achiedDateSet = trainingCompletedDate !== null

      // remove this after be change
      const temporaryStatus: DotStatus =
        // @ts-ignore
        status === 'overdue' ? 'notValid' : status

      const title = getDotTitle(
        temporaryStatus,
        trainingRequired,
        achiedDateSet
      )
      const dueForRefresh = getDueForRefresh(
        temporaryStatus,
        trainingDueDate,
        t
      )

      mappedRowsObject[skillId] = {
        dotStatus: temporaryStatus,
        dueForRefresh: trainingRequired ? dueForRefresh : undefined,
        trainingRequired,
        lastRefreshed: trainingCompletedDate,
        title: t(`skillsAndNeeds.dots.${title}`)
      }
    })

    return {
      key: element.carer.id,
      teamMember: {
        resident: {
          name: {
            first: element.carer.name.split(' ')[0],
            last: element.carer.name.split(' ')[1]
          },
          picture: carers?.find((carer) => carer.id === element.carer.id)
            ?.picture as unknown as string
        }
      },
      ...mappedRowsObject
    }
  })

  return rows
}

type Dictionary<record> = {
  [key in string]: record
}

export const getColumns = (data: SkillMatrixResponse) => {
  const totalRows = data?.content?.length
  const columnsStatus = getColumnsStatus(data?.content)

  const columns: TrainingColumn[] = data?.templates?.map((element) => {
    // calculate status here
    let status = ''
    if (typeof element.skill === 'object' && element.id) {
      const totalValidElements = columnsStatus[element.id] || 0

      status = `${totalValidElements}/${totalRows}`
    } else {
      status = ''
    }

    return {
      title: element.skill.name,
      key: element.id,
      dataIndex: element.id || '',
      status
    }
  })

  return columns
}

export const getDueOverDueByCarerRows = (
  data: DueOverdueByCarerResponse | undefined,
  carers: Carer[] | undefined
) =>
  data?.content?.map((element) => {
    const carerId = element.carer

    const teamMember = {
      id: carerId,
      name: data.carers[carerId].name,
      picture: carers?.find((carer) => carer.id === carerId)?.picture
    }

    const trainings = element.templates.map((templateId) => {
      const { templates } = data
      const template = templates[templateId]

      return template?.trainingName
    })
    const dates = element.trainingDueDates.map((date) => date || undefined)

    return {
      key: carerId,
      trainings: trainings || [],
      dates,
      teamMember: {
        onSelect: () => {
          window.location.href = `/organisation/team/${teamMember?.id}/profile`
        },
        resident: {
          name: teamMember?.name,
          picture: teamMember?.picture
        }
      } as ResidentCellProps
    }
  })

export const getDueOverDueByTrainingRows = (
  data: DueOverdueByTrainingResponse | undefined,
  carers: Carer[] | undefined
) =>
  data?.content?.map((element) => {
    const teamMembers = element.carers.map((carerId) => {
      // bad practice
      // we need the picture in response
      const teamMember = carers?.find((carerItem) => carerItem.id === carerId)
      return {
        id: carerId,
        name: data.carers[carerId].name,
        picture: teamMember?.picture
      }
    })

    const training: string = data.templates[element.template].trainingName
    const dates: string[] = element.trainingDueDates

    return {
      key: element.template,
      training,
      dates,
      teamMembers: teamMembers.map((teamMember) => ({
        onSelect: () => {
          window.location.href = `/organisation/team/${teamMember?.id}/profile`
        },
        resident: {
          name: teamMember?.name,
          picture: teamMember?.picture
        }
      })) as ResidentCellProps[]
    }
  })
